import { useState } from 'react';
import useSWR from 'swr';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../lib/api';

const PAGE_SIZE = 50;

export function OrganizationsPage() {
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const { data, mutate } = useSWR(
    ['admin/orgs', search, skip],
    () => adminApi.listOrgs({ search, limit: PAGE_SIZE, skip }),
    { keepPreviousData: true }
  );

  const onCreated = (id: string) => {
    setCreating(false);
    mutate();
    navigate(`/admin/orgs/${id}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-gray-100">Organizations</h1>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
            placeholder="Search by name or slug…"
            className="bg-gray-900 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100 w-64"
          />
          <button
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-sm rounded bg-blue-900/50 text-blue-100 border border-blue-800 hover:bg-blue-900"
          >
            New org
          </button>
        </div>
      </div>

      <div className="bg-gray-900 border border-gray-800 rounded">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800 text-gray-400 text-left">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Slug</th>
              <th className="px-4 py-2 font-medium">Members</th>
              <th className="px-4 py-2 font-medium">Bundle</th>
              <th className="px-4 py-2 font-medium">Store reflections</th>
              <th className="px-4 py-2 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {data?.organizations.map((o) => (
              <tr key={o._id} className="border-b border-gray-800 hover:bg-gray-800/40">
                <td className="px-4 py-2">
                  <Link to={`/admin/orgs/${o._id}`} className="text-blue-400 hover:underline">{o.name}</Link>
                </td>
                <td className="px-4 py-2 text-gray-300"><code className="text-xs">{o.slug}</code></td>
                <td className="px-4 py-2 text-gray-300">{o.memberCount ?? '—'}</td>
                <td className="px-4 py-2 text-gray-300">{o.defaultBundleKey || '—'}</td>
                <td className="px-4 py-2 text-gray-300">{o.shouldStoreReflections ? 'yes' : 'no'}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{new Date(o.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
            {data && data.organizations.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No organizations</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, data.total)} of {data.total}</span>
          <div className="flex gap-2">
            <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Prev</button>
            <button disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} className="px-3 py-1 rounded bg-gray-800 disabled:opacity-30">Next</button>
          </div>
        </div>
      )}

      {creating && <CreateOrgModal onClose={() => setCreating(false)} onCreated={onCreated} />}
    </div>
  );
}

function CreateOrgModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      const { organization } = await adminApi.createOrg({
        name: name.trim(),
        slug: slug.trim(),
        ownerEmail: ownerEmail.trim() || undefined,
      });
      onCreated(organization._id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded p-6 w-[28rem] space-y-4" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-100">New organization</h2>
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100" />
        </Field>
        <Field label="Slug" hint="3+ chars, lowercase letters/digits/hyphens">
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100" />
        </Field>
        <Field label="Owner email" hint="Optional — must be an existing user. They become 'owner'.">
          <input value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} className="w-full bg-gray-950 border border-gray-800 rounded px-3 py-1.5 text-sm text-gray-100" />
        </Field>
        {error && <div className="text-sm text-red-400">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded bg-gray-800 text-gray-300">Cancel</button>
          <button disabled={busy || !name || !slug} onClick={submit} className="px-3 py-1.5 text-sm rounded bg-blue-900/60 text-blue-100 disabled:opacity-50">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      {children}
      {hint && <div className="text-xs text-gray-600 mt-1">{hint}</div>}
    </label>
  );
}
